import { useEffect, useRef, useMemo } from "react";

import { PalDialog, PalButton, PalTextField, PalAutoComplete, PalTooltip } from "@palamar/fe-library";
import { withCardon } from "cardon";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { MdAdd, MdDelete, MdEdit } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { Box, Grid } from "@mui/material";

import PRButton from "~components/Generic/PRButton";
import PRContainer from "~components/Generic/PRContainer";
import PRTable from "~components/Generic/PRTable";
import { apiUrlProductQA } from "~constants";
import AlertHelper from "~helpers/AlertHelper";
import DateHelper from "~helpers/DateHelper";
import DialogHelper from "~helpers/DialogHelper";
import {
  deleteProductQuestion,
  fetchProductList,
  fetchDirectoryList,
  addProductQuestion,
  updateProductQuestion,
  fetchSingleProduct,
  fetchSingleDirectory,
} from "~store/productQA/actions";
import { selectCurrentProject } from "~store/user/selectors";

const ProductQA = () => {
  const dispatch = useDispatch();
  const tableRef = useRef(null);
  const { t } = useTranslation();
  const currentProject = useSelector(selectCurrentProject);

  useEffect(() => {
    tableRef.current.refresh();
  }, [currentProject]);

  const columns = useMemo(() => {
    const handleDelete = async (id) => {
      if (await DialogHelper.showQuestionDelete()) {
        await dispatch(deleteProductQuestion(currentProject.id, id));
        AlertHelper.showSuccess();
        tableRef.current.refresh();
      }
    };

    return [
      {
        label: t("dashboard.productQA.productTitle"),
        key: "product_title",
        render: (row) => (
          <Box alignItems="center" display="flex" gap={1}>
            <a href={row.product_detail?.url} rel="noopener noreferrer" target="_blank">
              <img
                alt={row.product_detail?.title}
                src={row.product_detail?.image_url}
                style={{ width: 40, height: 40 }}
              />
            </a>
            <PalTooltip title={row.product_detail?.title}>
              <span>{row.product_detail?.title}</span>
            </PalTooltip>
          </Box>
        ),
      },
      {
        label: t("dashboard.productQA.question"),
        key: "question",
      },
      {
        label: t("dashboard.productQA.answer"),
        key: "answer",
      },

      {
        label: t("common.created"),
        key: "created",
        noWrap: true,
        render: (row) => (
          <span>
            <PalTooltip title={DateHelper.getDateTimeLocal(row.created).fromNow()}>
              {DateHelper.getDateTimeLocal(row.created).format("LLT")}
            </PalTooltip>
          </span>
        ),
      },
      {
        label: t("dashboard.productQA.actions"),
        key: "actions",
        render: (row) => (
          <div className="d-flex justify-content-center gap-1">
            <PRButton
              outline
              color="primary"
              icon={MdEdit}
              size="sm"
              tooltipText={t("common.edit")}
              onClick={async () => {
                const response = await AddOrEditProductQuestionModal.show({
                  data: row,
                });
                if (response) {
                  tableRef.current.refresh();
                }
              }}
            />
            <PRButton
              outline
              color="danger"
              icon={MdDelete}
              size="sm"
              tooltipText={t("common.delete")}
              onClick={() => handleDelete(row.id)}
            />
          </div>
        ),
      },
    ];
  }, [t, dispatch, currentProject?.id]);

  const filters = useMemo(
    () => [
      {
        key: "product",
        label: t("dashboard.productQA.product"),
        onFetch: async (searchText, callback, signal) => {
          const response = await dispatch(fetchProductList(currentProject.id, searchText, signal));
          const options = response.results.map((item) => ({
            label: item.title,
            value: item.id,
          }));
          return options;
        },
      },
      {
        key: "directory",
        label: t("dashboard.productQA.directory"),
        onFetch: async (searchText, callback, signal) => {
          const response = await dispatch(fetchDirectoryList(currentProject.id, searchText, signal));
          const options = response.results.map((item) => ({
            label: item.name,
            value: item.id,
          }));
          return options;
        },
      },
    ],
    [dispatch, currentProject?.id, t]
  );

  const actions = [
    {
      label: t("dashboard.productQA.add"),
      icon: MdAdd,
      color: "success",
      onClick: async () => {
        const response = await AddOrEditProductQuestionModal.show();
        if (response) {
          tableRef.current.refresh();
        }
      },
    },
  ];

  return (
    <PRContainer
      actions={actions}
      description={t("dashboard.productQA.description")}
      name={t("dashboard.productQA.title")}
    >
      <PRTable
        ref={tableRef}
        columns={columns}
        filters={filters}
        url={apiUrlProductQA.getQuestions.format(currentProject.id)}
      />
    </PRContainer>
  );
};

const AddOrEditProductQuestionModal = withCardon(
  function AddOrEditProductQuestionModalContent({ get, data }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const currentProject = useSelector(selectCurrentProject);

    const formik = useFormik({
      initialValues: {
        question: data?.question || "",
        answer: data?.answer || "",
        product: data?.product || null,
        directory: data?.directory || null,
      },
      validationSchema: Yup.object({
        question: Yup.string().required(t("formik.required")),
        answer: Yup.string().required(t("formik.required")),
        product: Yup.mixed().nullable(),
        directory: Yup.mixed()
          .nullable()
          .test("required", t("formik.required"), function (value) {
            //only required if product is not selected
            const { product } = this.parent;
            return product || value;
          }),
      }),
      onSubmit: async (values) => {
        const payload = {
          question: values.question,
          answer: values.answer,
          product: values.product,
          directory: values.directory,
        };

        if (data?.id) {
          await dispatch(updateProductQuestion(currentProject.id, data.id, payload));
        } else {
          await dispatch(addProductQuestion(currentProject.id, payload));
        }

        get(true)();
      },
    });

    const loadProductOptions = async (searchText, signal) => {
      const response = await dispatch(fetchProductList(currentProject.id, searchText, signal));
      const fetchedOptions = response.results.map((item) => ({
        label: item.title,
        value: item.id,
      }));

      if (formik.values.product && !fetchedOptions.find((item) => item.value === formik.values.product)) {
        const product = await dispatch(fetchSingleProduct(currentProject.id, formik.values.product, { signal }));
        if (product) {
          fetchedOptions.push({
            label: product.title,
            value: product.id,
          });
        }
      }

      return fetchedOptions;
    };

    const loadDirectoryOptions = async (searchText, signal) => {
      const response = await dispatch(fetchDirectoryList(currentProject.id, searchText, signal));
      const fetchedOptions = response.results.map((item) => ({
        label: item.name,
        value: item.id,
      }));

      if (formik.values.directory && !fetchedOptions.find((item) => item.value === formik.values.directory)) {
        const directory = await dispatch(fetchSingleDirectory(currentProject.id, formik.values.directory, { signal }));
        if (directory) {
          fetchedOptions.push({
            label: directory.name,
            value: directory.id,
          });
        }
      }

      return fetchedOptions;
    };

    return (
      <PalDialog fullWidth open maxWidth="sm" onClose={get(false)}>
        <PalDialog.Title>{data ? t("dashboard.productQA.edit") : t("dashboard.productQA.add")}</PalDialog.Title>
        <PalDialog.Content>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <PalTextField
                fullWidth
                multiline
                required
                invalid={formik.touched.question && formik.errors.question}
                label={t("dashboard.productQA.question")}
                maxRows={4}
                minRows={2}
                name="question"
                value={formik.values.question}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <PalTextField
                fullWidth
                multiline
                required
                invalid={formik.touched.answer && formik.errors.answer}
                label={t("dashboard.productQA.answer")}
                maxRows={4}
                minRows={2}
                name="answer"
                value={formik.values.answer}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <PalAutoComplete
                isPrimitiveValue
                lazy
                invalid={formik.touched.product && formik.errors.product}
                label={t("dashboard.productQA.product")}
                loadOptions={loadProductOptions}
                placeholder={t("dashboard.productQA.product")}
                value={formik.values.product}
                onChange={(e, value) => formik.setFieldValue("product", value)}
              />
            </Grid>
            <Grid item xs={12}>
              <PalAutoComplete
                isPrimitiveValue
                lazy
                invalid={formik.touched.directory && formik.errors.directory}
                label={t("dashboard.productQA.directory")}
                loadOptions={loadDirectoryOptions}
                placeholder={t("dashboard.productQA.directory")}
                value={formik.values.directory}
                onChange={(e, value) => formik.setFieldValue("directory", value)}
              />
            </Grid>
          </Grid>
        </PalDialog.Content>
        <PalDialog.Actions>
          <PalButton color="primary" variant="contained" onClick={formik.handleSubmit}>
            {t("common.save")}
          </PalButton>
          <PalButton onClick={get(false)}>{t("common.cancel")}</PalButton>
        </PalDialog.Actions>
      </PalDialog>
    );
  },
  {
    destroyOnHide: true,
  }
);

export default ProductQA;
